import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserSession,
  CognitoUserAttribute,
} from "amazon-cognito-identity-js";
import { setAuthToken, setUnauthorizedHandler } from "../api/client.js";

const POOL = new CognitoUserPool({
  UserPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID ?? "us-east-1_0HTTDMjhy",
  ClientId:   import.meta.env.VITE_COGNITO_CLIENT_ID   ?? "31hrpfftl8fr00itqr8p2v3fgj",
});

interface AuthUser { email: string; userId: string; token: string }

interface AuthCtx {
  user: AuthUser | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  /** Step 1: creates Cognito account, sends verification email */
  signup: (email: string, password: string) => Promise<void>;
  /** Step 2: confirms email with the code Cognito sent */
  confirmSignUp: (email: string, code: string) => Promise<void>;
  logout: () => void;
  changePassword: (oldPassword: string, newPassword: string) => Promise<void>;
  /** Step 1: sends verification code to newEmail */
  requestEmailChange: (newEmail: string) => Promise<void>;
  /** Step 2: confirms the code and updates user state */
  confirmEmailChange: (newEmail: string, code: string) => Promise<void>;
  /** Step 1: sends password-reset code to the email (Cognito "forgotPassword") */
  forgotPassword: (email: string) => Promise<void>;
  /** Step 2: confirms code and sets a new password */
  confirmForgotPassword: (email: string, code: string, newPassword: string) => Promise<void>;
  error: string | null;
}

const Ctx = createContext<AuthCtx>(null!);
export const useAuth = () => useContext(Ctx);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser]       = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);

  const applySession = useCallback((session: CognitoUserSession, email: string) => {
    const token  = session.getIdToken().getJwtToken();
    const userId = session.getIdToken().payload["sub"] as string;
    setAuthToken(token);
    setUser({ email, userId, token });
  }, []);

  // Restore session on mount
  useEffect(() => {
    const cognitoUser = POOL.getCurrentUser();
    if (!cognitoUser) { setLoading(false); return; }
    cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session?.isValid()) { setLoading(false); return; }
      const email = session.getIdToken().payload["email"] as string;
      applySession(session, email);
      setLoading(false);
    });
  }, [applySession]);

  // Refresh the ID token every 50 minutes so requests don't start failing with
  // 403s after the default 1-hour Cognito token lifetime. getSession() auto-
  // exchanges a valid refresh token for a new id/access pair; we just need to
  // push the new jwt into the API client. Refresh tokens last 30 days by
  // default, so users only need to re-login every few weeks.
  useEffect(() => {
    if (!user) return;
    const tick = () => {
      const cognitoUser = POOL.getCurrentUser();
      if (!cognitoUser) return;
      cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
        if (err || !session?.isValid()) return;
        const token = session.getIdToken().getJwtToken();
        setAuthToken(token);
      });
    };
    const id = setInterval(tick, 50 * 60 * 1000); // 50 min
    return () => clearInterval(id);
  }, [user]);

  const login = useCallback((email: string, password: string) => {
    setError(null);
    return new Promise<void>((resolve, reject) => {
      const cognitoUser = new CognitoUser({ Username: email, Pool: POOL });
      cognitoUser.authenticateUser(
        new AuthenticationDetails({ Username: email, Password: password }),
        {
          onSuccess: (session) => { applySession(session, email); resolve(); },
          onFailure: (err) => { setError(err.message ?? "Login failed"); reject(err); },
        },
      );
    });
  }, [applySession]);

  const signup = useCallback((email: string, password: string): Promise<void> => {
    setError(null);
    return new Promise((resolve, reject) => {
      const emailAttr = new CognitoUserAttribute({ Name: "email", Value: email });
      POOL.signUp(email, password, [emailAttr], [], (err) => {
        if (err) { setError(err.message ?? "Sign up failed"); reject(err); }
        else resolve();
      });
    });
  }, []);

  const confirmSignUp = useCallback((email: string, code: string): Promise<void> => {
    setError(null);
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({ Username: email, Pool: POOL });
      cognitoUser.confirmRegistration(code, true, (err) => {
        if (err) { setError(err.message ?? "Verification failed"); reject(err); }
        else resolve();
      });
    });
  }, []);

  const logout = useCallback(() => {
    POOL.getCurrentUser()?.signOut();
    setAuthToken(null);
    setUser(null);
    // Clear handler so it doesn't fire after logout
    setUnauthorizedHandler(() => {});
  }, []);

  // Register a global 401 handler so expired tokens redirect to login
  // rather than silently showing empty state.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      POOL.getCurrentUser()?.signOut();
      setAuthToken(null);
      setUser(null);
      window.location.href = "/login";
    });
  }, []);

  const changePassword = useCallback((oldPassword: string, newPassword: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      const cognitoUser = POOL.getCurrentUser();
      if (!cognitoUser) { reject(new Error("Not signed in")); return; }
      // Need a valid session before calling changePassword
      cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
        if (err || !session?.isValid()) { reject(new Error("Session expired — please log in again")); return; }
        cognitoUser.changePassword(oldPassword, newPassword, (err2, _result) => {
          if (err2) reject(new Error(err2.message ?? "Password change failed"));
          else resolve();
        });
      });
    });
  }, []);

  const getAuthedUser = useCallback((): Promise<CognitoUser> => {
    return new Promise((resolve, reject) => {
      const cognitoUser = POOL.getCurrentUser();
      if (!cognitoUser) { reject(new Error("Not signed in")); return; }
      cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
        if (err || !session?.isValid()) { reject(new Error("Session expired — please log in again")); return; }
        resolve(cognitoUser);
      });
    });
  }, []);

  const requestEmailChange = useCallback(async (newEmail: string): Promise<void> => {
    const cognitoUser = await getAuthedUser();
    return new Promise((resolve, reject) => {
      const attr = new CognitoUserAttribute({ Name: "email", Value: newEmail });
      cognitoUser.updateAttributes([attr], (err) => {
        if (err) reject(new Error(err.message ?? "Failed to request email change"));
        else resolve();
      });
    });
  }, [getAuthedUser]);

  const confirmEmailChange = useCallback(async (newEmail: string, code: string): Promise<void> => {
    const cognitoUser = await getAuthedUser();
    return new Promise((resolve, reject) => {
      cognitoUser.verifyAttribute("email", code, {
        onSuccess: () => {
          // Refresh the token so the new email is reflected
          cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
            if (!err && session?.isValid()) applySession(session, newEmail);
          });
          resolve();
        },
        onFailure: (err) => reject(new Error(err.message ?? "Invalid verification code")),
      });
    });
  }, [getAuthedUser, applySession]);

  const forgotPassword = useCallback((email: string): Promise<void> => {
    setError(null);
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({ Username: email, Pool: POOL });
      cognitoUser.forgotPassword({
        onSuccess: () => resolve(),
        onFailure: (err) => { setError(err.message ?? "Could not start password reset"); reject(err); },
      });
    });
  }, []);

  const confirmForgotPassword = useCallback((email: string, code: string, newPassword: string): Promise<void> => {
    setError(null);
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({ Username: email, Pool: POOL });
      cognitoUser.confirmPassword(code, newPassword, {
        onSuccess: () => resolve(),
        onFailure: (err) => { setError(err.message ?? "Password reset failed"); reject(err); },
      });
    });
  }, []);

  return <Ctx.Provider value={{ user, loading, login, signup, confirmSignUp, logout, changePassword, requestEmailChange, confirmEmailChange, forgotPassword, confirmForgotPassword, error }}>{children}</Ctx.Provider>;
}
